import classNames from "classnames";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { TABLE_ROW_HOVER_CLASSES } from "@/lib/styleHelpers";

export const TABLE_CARD_BODY_CELL = "py-2 h-12 align-middle text-left text-body-medium text-sys-brand-on-surface";

export interface TableCardRowProps {
  columnCount: number;
  mobile: ReactNode;
  children: ReactNode;
  rowTestId?: string;
  className?: string;
  onClick?: () => void;
  onContextMenu?: (event: MouseEvent<HTMLTableRowElement>) => void;
  ariaLabel?: string;
  /**
   * When `onClick` is set, the row is mouse-clickable and focusable with Enter/Space.
   * Set `asButton={false}` when the row also contains a button so the `<tr>` is not
   * announced as a nested button; keyboard activation still works on the focused row.
   * Pass `ariaLabel` for any interactive row so screen readers have an accessible name
   * even when `asButton` is false.
   */
  asButton?: boolean;
  /** Expanded state for expandable rows; forwarded as aria-expanded. */
  ariaExpanded?: boolean;
  /** When set, overrides `useIsCompactLayout` for stacked vs tabular row rendering. */
  stackedLayout?: boolean;
}

export default function TableCardRow({
  columnCount,
  mobile,
  children,
  rowTestId = "table-card-row",
  className,
  onClick,
  onContextMenu,
  ariaLabel,
  asButton = true,
  ariaExpanded,
  stackedLayout
}: TableCardRowProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const useStackedLayout = stackedLayout ?? isCompactLayout;
  const isInteractive = Boolean(onClick);
  const isButtonRow = isInteractive && asButton;

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (!onClick) {
      return;
    }

    // Ignore key events from nested controls (overflow menus, links, etc.).
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <tr
      className={classNames(
        "border-t border-sys-brand-surface-container-highest",
        "bg-sys-brand-surface",
        isInteractive && `cursor-pointer ${TABLE_ROW_HOVER_CLASSES}`,
        className
      )}
      data-testid={rowTestId}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      role={isButtonRow ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? ariaLabel : undefined}
      {...(ariaExpanded != null ? { "aria-expanded": ariaExpanded } : {})}
    >
      {useStackedLayout ? (
        <td colSpan={columnCount} className="min-w-0 w-full py-3 align-top text-body-medium text-sys-brand-on-surface">
          {mobile}
        </td>
      ) : (
        children
      )}
    </tr>
  );
}

export interface TableCardMobileStackProps {
  lines: ReactNode[];
}

export function TableCardMobileStack({ lines }: TableCardMobileStackProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {lines.map((line, index) => (
        <div
          key={index}
          className={classNames({
            "text-body-small text-sys-brand-on-surface-variant": index > 0
          })}
        >
          {line}
        </div>
      ))}
    </div>
  );
}
